import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { MapPin, Phone, Navigation2, Search } from 'lucide-react';
type PageView = 'home' | 'archive' | 'detail' | 'configurator' | 'warranty' | 'instructions' | 'contact' | 'media' | 'dealers';
interface DealerLocatorPageProps {
  onNavigate: (page: PageView, productId?: number) => void;
}
interface Dealer {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  distance?: string;
}
const dealers: Dealer[] = [{
  id: 1,
  name: 'Off-Road Outfitters Fresno',
  address: '4521 N Blackstone Ave',
  city: 'Fresno',
  state: 'CA',
  zip: '93726',
  phone: '(559) 555-0101',
  distance: '2.3 mi'
}, {
  id: 2,
  name: 'Valley Truck & 4x4',
  address: '1890 Shaw Ave',
  city: 'Clovis',
  state: 'CA',
  zip: '93611',
  phone: '(559) 555-0202',
  distance: '8.7 mi'
}, {
  id: 3,
  name: 'Sierra Suspension Specialists',
  address: '7823 N Cedar Ave',
  city: 'Fresno',
  state: 'CA',
  zip: '93720',
  phone: '(559) 555-0303',
  distance: '12.1 mi'
}, {
  id: 4,
  name: 'Central Valley Lift & Performance',
  address: '2340 W Bullard Ave',
  city: 'Fresno',
  state: 'CA',
  zip: '93711',
  phone: '(559) 555-0404',
  distance: '15.4 mi'
}, {
  id: 5,
  name: 'Madera Off-Road Center',
  address: '1456 Gateway Dr',
  city: 'Madera',
  state: 'CA',
  zip: '93637',
  phone: '(559) 555-0505',
  distance: '22.8 mi'
}];
export function DealerLocatorPage({
  onNavigate
}: DealerLocatorPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-396">
      <Header data-id="element-397" />
      <Navigation onNavigate={onNavigate} data-id="element-398" />

      <main className="flex-grow" data-id="element-399">
        {/* Hero */}
        <div className="bg-charcoal text-white py-16 relative overflow-hidden" data-id="element-400">
          <div className="absolute inset-0 bg-hero-pattern opacity-10 bg-cover bg-center" data-id="element-401" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-402" />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 relative z-10" data-id="element-403">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-404">
              <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-405">
                Home
              </button>
              <span data-id="element-406">/</span>
              <span className="text-white" data-id="element-407">Dealer Locator</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase italic tracking-tighter" data-id="element-408">
              Find a <span className="text-primary" data-id="element-409">Dealer</span>
            </h1>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16" data-id="element-410">
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-10" data-id="element-411">
            Locate an authorized Pro One Design dealer near you. Our network of
            certified installers can help you choose the right kit and ensure
            proper installation.
          </p>

          {/* Search */}
          <div className="bg-white border border-gray-200 p-6 mb-12" data-id="element-412">
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-413">
              <div className="relative flex-grow" data-id="element-414">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-415" />
                <input type="text" placeholder="Enter city, state, or ZIP code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-primary transition-colors" data-id="element-416" />
              </div>
              <button className="px-8 py-3 bg-primary text-white font-heading font-bold uppercase italic tracking-wider hover:bg-charcoal transition-colors skew-x-[-10deg] flex-shrink-0" data-id="element-417">
                <span className="block skew-x-[10deg]" data-id="element-418">Search</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-419">
            {/* Dealers List */}
            <div className="lg:col-span-2 space-y-4" data-id="element-420">
              {dealers.map(dealer => <div key={dealer.id} className="bg-white border border-gray-200 p-6 flex flex-col sm:flex-row gap-6 hover:border-primary/50 transition-colors" data-id="element-421">
                  <div className="flex-grow" data-id="element-422">
                    <h3 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-2" data-id="element-423">
                      {dealer.name}
                    </h3>
                    <div className="space-y-2 text-sm text-gray-600 mb-4" data-id="element-424">
                      <div className="flex items-start gap-2" data-id="element-425">
                        <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" data-id="element-426" />
                        <span data-id="element-427">
                          {dealer.address}
                          <br data-id="element-428" />
                          {dealer.city}, {dealer.state} {dealer.zip}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-429">
                        <Phone className="w-4 h-4 text-primary flex-shrink-0" data-id="element-430" />
                        <a href={`tel:${dealer.phone.replace(/\D/g, '')}`} className="hover:text-primary transition-colors" data-id="element-431">
                          {dealer.phone}
                        </a>
                      </div>
                    </div>
                    {dealer.distance && <p className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-432">
                        {dealer.distance} away
                      </p>}
                  </div>
                  <div className="flex sm:flex-col gap-3 flex-shrink-0" data-id="element-433">
                    <button className="px-6 py-2 border-2 border-charcoal text-charcoal font-heading font-bold uppercase italic tracking-wider hover:bg-charcoal hover:text-white transition-colors text-sm whitespace-nowrap" data-id="element-434">
                      Call Now
                    </button>
                    <button className="px-6 py-2 bg-charcoal text-white font-heading font-bold uppercase italic tracking-wider hover:bg-primary transition-colors text-sm flex items-center gap-2 justify-center whitespace-nowrap" data-id="element-435">
                      <Navigation2 className="w-4 h-4" data-id="element-436" /> Directions
                    </button>
                  </div>
                </div>)}
            </div>

            {/* Map Placeholder */}
            <div className="lg:col-span-1" data-id="element-437">
              <div className="sticky top-8 w-full h-[600px] bg-charcoal relative overflow-hidden border border-gray-700" data-id="element-438">
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-439">
                  <div className="text-center" data-id="element-440">
                    <MapPin className="w-12 h-12 text-primary mx-auto mb-3" data-id="element-441" />
                    <p className="text-white font-heading font-bold text-xl uppercase italic mb-1" data-id="element-442">
                      Dealer Map
                    </p>
                    <p className="text-gray-400 text-xs" data-id="element-443">Fresno, CA Area</p>
                  </div>
                </div>
                <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,121,30,0.3) 1px, transparent 0)',
                backgroundSize: '30px 30px'
              }} data-id="element-444" />
              </div>
            </div>
          </div>

          {/* Become a Dealer CTA */}
          <div className="mt-16 bg-charcoal text-white p-10 flex flex-col md:flex-row items-center justify-between gap-6 border-t-4 border-primary" data-id="element-445">
            <div data-id="element-446">
              <h3 className="text-3xl font-heading font-bold uppercase italic mb-2" data-id="element-447">
                Interested in Becoming a Dealer?
              </h3>
              <p className="text-gray-400" data-id="element-448">
                Join our network of authorized installers and offer Pro One
                Design products to your customers.
              </p>
            </div>
            <button onClick={() => onNavigate('contact')} className="px-8 py-3 bg-primary text-white font-heading font-bold uppercase italic tracking-wider hover:bg-white hover:text-charcoal transition-colors skew-x-[-10deg] flex-shrink-0" data-id="element-449">
              <span className="block skew-x-[10deg]" data-id="element-450">Contact Us</span>
            </button>
          </div>
        </div>
      </main>

      <Footer onNavigate={onNavigate} data-id="element-451" />
    </div>;
}